import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { KeyRound, ArrowLeft, Home, MapPin, Phone, Loader2, Calendar, Receipt, FileText, Download } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { useProperties } from '../hooks/useProperties';
import { Property } from '../types/property';
import { ReservationsList } from '../components/concierge/ReservationsList';
import { ExpensesList } from '../components/concierge/ExpensesList';
import { ConciergerieMonthlyBulletinPDF } from '../components/pdf/ConciergerieMonthlyBulletinPDF';
import { conciergeAPI } from '../api/concierge';

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const PROPERTY_TYPES: Record<string, string> = {
  'appartement': 'Appartement',
  'maison': 'Maison',
  'villa': 'Villa',
  'terrain': 'Terrain',
  'local-commercial': 'Local Commercial',
  'immeuble': 'Immeuble',
  'promotion': 'Promotion'
};

type ViewMode = 'reservations' | 'expenses';

export const ConciergerieDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getPropertyById, isLoading } = useProperties();
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [viewMode, setViewMode] = useState<ViewMode>('reservations');
  const [generatingBulletin, setGeneratingBulletin] = useState<boolean>(false);

  const property = id ? getPropertyById(id) : null;

  const handleGenerateBulletin = async () => {
    if (!property) return;

    try {
      setGeneratingBulletin(true);
      const year = new Date().getFullYear();
      const month = selectedMonth + 1;

      const bulletinData = await conciergeAPI.getBulletinData(property.id, year, month);

      const doc = <ConciergerieMonthlyBulletinPDF
        agency={bulletinData.agency}
        property={bulletinData.property}
        reservations={bulletinData.reservations}
        expenses={bulletinData.expenses}
        month={month}
        year={year}
      />;

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Bulletin ${MONTHS[selectedMonth]} ${year} - ${property.reference || property.owner_name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de la génération du bulletin:', error);
      alert('Erreur lors de la génération du bulletin PDF');
    } finally {
      setGeneratingBulletin(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/conciergerie')}
          className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Retour à la liste</span>
        </button>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">Bien non trouvé</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/conciergerie')}
        className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Retour à la liste</span>
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 space-y-4 sm:space-y-0">
          <div className="flex items-start space-x-3 sm:space-x-4">
            <div className="p-2 sm:p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg shrink-0">
              <KeyRound className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                #{property.reference_number || property.id}
              </h1>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  <Home className="w-4 h-4 shrink-0" />
                  <span className="truncate">{PROPERTY_TYPES[property.property_category] || property.property_category}</span>
                </div>
                <div className="flex items-start space-x-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                  <span className="break-words">{property.address}, {property.city}, {property.wilaya}</span>
                </div>
                <div className="flex flex-col space-y-1 text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 shrink-0" />
                    <span className="font-medium">Propriétaire: {property.owner_name}</span>
                  </div>
                  {property.owner_phone && (
                    <span className="ml-6 text-blue-600 dark:text-blue-400 font-medium">{property.owner_phone}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 sm:pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex space-x-2 overflow-x-auto scrollbar-hide overscroll-x-contain pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
              {MONTHS.map((month, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedMonth(index)}
                  className={`px-3 py-2 sm:px-4 rounded-lg font-medium transition-colors whitespace-nowrap text-sm sm:text-base touch-manipulation ${
                    selectedMonth === index
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {month}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 mb-6">
            <div className="flex space-x-2 sm:space-x-4">
              <button
                onClick={() => setViewMode('reservations')}
                className={`flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 rounded-lg font-medium transition-colors flex-1 sm:flex-initial touch-manipulation ${
                  viewMode === 'reservations'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Calendar className="w-5 h-5 shrink-0" />
                <span className="text-sm sm:text-base">Réservations</span>
              </button>
              <button
                onClick={() => setViewMode('expenses')}
                className={`flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 rounded-lg font-medium transition-colors flex-1 sm:flex-initial touch-manipulation ${
                  viewMode === 'expenses'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Receipt className="w-5 h-5 shrink-0" />
                <span className="text-sm sm:text-base">Dépenses</span>
              </button>
            </div>

            <button
              onClick={handleGenerateBulletin}
              disabled={generatingBulletin}
              className={`flex items-center justify-center space-x-2 px-4 py-3 sm:py-2 rounded-lg font-medium transition-all border-2 touch-manipulation ${
                generatingBulletin
                  ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-600/20 dark:hover:bg-blue-500/30'
              }`}
            >
              {generatingBulletin ? (
                <>
                  <Loader2 className="w-4 h-4 sm:w-4 sm:h-4 animate-spin shrink-0" />
                  <span className="text-sm">Génération...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 sm:w-4 sm:h-4 shrink-0" />
                  <span className="text-sm">Générer Bulletin</span>
                </>
              )}
            </button>
          </div>

          <div>
            {viewMode === 'reservations' ? (
              <ReservationsList
                propertyId={property.id}
                year={new Date().getFullYear()}
                month={selectedMonth + 1}
              />
            ) : (
              <ExpensesList
                propertyId={property.id}
                year={new Date().getFullYear()}
                month={selectedMonth + 1}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
